import DrumIcon from '@/CustomIcons/DrumIcon'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import { cn } from '@/utlis/cn'
import { animate as framerAnimate, motion, useMotionValue, useTransform } from 'framer-motion'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const DOT_ANIMATION_STAGGER_S = 0.1

export type ApiStatsResponse = {
    dailySessionStats: Array<{
        _id: string
        sessionCount: number
        sessionMinutes: number
    }>
    collaboratorsAvailableCount: number
}

type StatsCardProps = {
    stats: ApiStatsResponse | null
}
export default function StatsCard(props: StatsCardProps): JSX.Element {
    return (
        <div className="w-full h-full flex flex-col gap-[4px]">
            <SessionsHostedCard
                sessionCountByDay={props.stats?.dailySessionStats?.map((stat) => stat.sessionCount) || []}
            />
            <div className="flex gap-[4px] h-[50%]">
                <SpentInSession
                    timeSpentByDay={props.stats?.dailySessionStats?.map((stat) => stat.sessionMinutes) || []}
                />
                <PeopleReadyCard people={props.stats?.collaboratorsAvailableCount ?? 0} />
            </div>
        </div>
    )
}

function useAnimatedCount(initialValue: number, finalValue: number, duration: number) {
    const animationCount = useMotionValue(initialValue)
    const roundedCount = useTransform(() => Math.round(animationCount.get()))

    useEffect(() => {
        const controls = framerAnimate(animationCount, finalValue, { duration })
        return () => controls.stop()
    }, [animationCount, duration, finalValue])

    return roundedCount
}

// Container variants with staggerChildren
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: DOT_ANIMATION_STAGGER_S, // 10ms between each child
        },
    },
}

// Individual dot variants
const dotVariants = {
    hidden: {
        opacity: 0,
        scale: 0,
    },
    visible: {
        opacity: 1,
        scale: 1,
    },
}

function SessionsHostedCard({ sessionCountByDay }: { sessionCountByDay: number[] }): JSX.Element {
    const { t } = useTranslation()
    const totalHostedSessions = sessionCountByDay.reduce((acc, val) => acc + val, 0)
    const sessionCountDisplay = useAnimatedCount(0, totalHostedSessions, 1)

    return (
        <div className="w-full h-[50%] flex flex-col rounded-[26px] bg-[rgba(21,21,21,0.85)] border-muse-toplit  shadow-glow px-[15px] lg:px-[25px] pt-[15px] lg:pt-[25px]">
            <div className="flex justify-between">
                <p className="t-sm-light lg:t-base-light">{t('Sessions Hosted')}</p>
                <p className="t-sm-light lg:t-base-light opacity-50">{t('Last 30 days')}</p>
            </div>
            <div className="flex flex-col w-full grow justify-end">
                <div className="flex justify-between">
                    <motion.div className="font-light text-[30px] pb-[8px]">{sessionCountDisplay}</motion.div>
                    <motion.div
                        className="flex gap-[4px] items-end pb-[20px]"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {sessionCountByDay.map((sessionCount, index) =>
                            sessionCount > 0 ? (
                                <motion.div
                                    className="flex flex-col-reverse gap-[4px] "
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    key={`session-${index}-${sessionCount}`}
                                >
                                    {Array(Math.min(sessionCount, 9))
                                        .fill(0)
                                        .map((_, i) => (
                                            <motion.div
                                                key={`session-${index}-${sessionCount}-${i}`}
                                                className="h-[4px] w-[4px] rounded-full bg-[rgba(133,224,163,1)]"
                                                variants={dotVariants}
                                            />
                                        ))}
                                </motion.div>
                            ) : (
                                <motion.div
                                    variants={dotVariants}
                                    key={`empty-day-${index}`}
                                    className="h-[4px] w-[4px] rounded-full bg-[rgba(255,255,255,0.2)]"
                                />
                            )
                        )}
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

function SpentInSession({ timeSpentByDay }: { timeSpentByDay: number[] }): JSX.Element {
    const { t } = useTranslation()
    const totalTimeSpent = timeSpentByDay.reduce((acc, val) => acc + val / 60, 0)
    const totalTimeDisplay = useAnimatedCount(0, totalTimeSpent, 31 * DOT_ANIMATION_STAGGER_S)

    return (
        <div className="w-full h-full flex flex-col justify-between rounded-[26px] bg-[rgba(21,21,21,0.85)] border-muse-toplit  shadow-glow  px-[15px] lg:px-[25px] pt-[15px] lg:pt-[25px]">
            <div className="flex justify-between">
                <div className="flex flex-col t-sm-light lg:t-base-light">
                    <Trans i18nKey="<1>Spent</1> <2>In Session</2>" t={t}>
                        <p>Spent</p>
                        <p>In Session</p>
                    </Trans>
                </div>
                <p className="t-sm-light lg:t-base-light opacity-50">{t('Last 30 days')}</p>
            </div>
            <div className="flex justify-between">
                <div className="flex gap-[8px] pb-[4px]">
                    <motion.span className="flex flex-col justify-end font-light text-[30px] h-full">
                        {totalTimeDisplay}
                    </motion.span>
                    <span className="flex flex-col justify-end opacity-50 h-[85%]">h</span>
                </div>
                <motion.div
                    className="flex w-[42px] items-end content-end gap-[4px] flex-wrap pb-[16px]"
                    variants={{
                        hidden: { opacity: 0 },
                        visible: {
                            opacity: 1,
                            transition: {
                                staggerChildren: 0.05, // 10ms between each child
                            },
                        },
                    }}
                    initial="hidden"
                    animate="visible"
                >
                    {timeSpentByDay.map((hours, index) => (
                        <motion.div
                            key={`hours-by-day-${index}`}
                            variants={dotVariants}
                            className={cn(
                                'h-[4px] w-[4px] rounded-full',
                                hours > 0 ? 'bg-[rgba(133,224,163,1)]' : 'bg-[rgba(255,255,255,0.2)]'
                            )}
                        />
                    ))}
                </motion.div>
            </div>
        </div>
    )
}

function PeopleReadyCard({ people }: { people: number }): JSX.Element {
    const { t } = useTranslation()
    const peopleReadyDisplay = useAnimatedCount(0, people, 1)

    return (
        <div className="w-full h-full flex flex-col justify-between rounded-[26px] bg-[rgba(21,21,21,0.85)] border-muse-toplit shadow-glow px-[15px] lg:px-[25px] pt-[15px] lg:pt-[25px]">
            <div className="flex justify-between w-full">
                <div className="flex flex-col t-sm-light lg:t-base-light">
                    <Trans i18nKey="<1>People Ready</1> <2>To Collaborate</2>" t={t}>
                        <p>People Ready</p>
                        <p>To Collaborate</p>
                    </Trans>
                </div>
            </div>
            <motion.div className="flex flex-col w-full grow justify-end pb-[4px] font-light text-[30px]">
                {peopleReadyDisplay}
            </motion.div>
            <div className="absolute bottom-0 right-0 w-[15%] h-[25%] bg-[url('/images/sparkle.webp')] bg-no-repeat"></div>
            <div className="w-[65px] h-[65px] absolute -bottom-8 right-10 bg-[rgba(255,255,255,0.05)] border-muse-toplit shadow-glow rounded-full opacity-25">
                <DrumIcon className="absolute left-[20%] top-[20%] scale-200" />
            </div>
            <div className="w-[55px] h-[55px] opacity-20 absolute bottom-6 -right-4 bg-[radial-gradient(circle_at_bottom,rgba(255,100,100,0.3)_0%,transparent_40%)] bg-[rgba(36,36,36,1)] border-muse-toplit shadow-glow rounded-full flex items-center justify-center">
                <MicrophoneIcon withGradient />
            </div>
        </div>
    )
}
